import React from 'react';
import { observer } from 'mobx-react';
import { ApiPrice, ApiPriceModifierType, ApiPriceType } from '@ibe/api';
import Keys from '../../../../../_Translations/generated/package.en.t.json.keys';
import fallback from '../../../../../_Translations/generated/package.en.t.json';
import useTranslation from '../../../../Hooks/useTranslation';
import { Price } from '../../../UI/Price/Price';
import useConfig from '../../../../Hooks/useConfig';
import PackageCustomizerUtil from '../../../../Modules/Customizer/PackageCustomizer/PackageCustomizerUtil';

export enum PricePerDisplay {
  PASSENGER = 'passenger',
  DAY = 'day'
}

export interface Props {
  totalPrice: ApiPrice;
  pricePerDisplay?: PricePerDisplay | string;
  pricePer?: ApiPrice;
  showFromPrefix?: boolean;
  showSign?: boolean;
  isTotalFromPrice?: boolean | false;
  showTaxText?: boolean | true;
  customTaxText?: string;
  priceType?: ApiPriceType | undefined;
  mode?: 'priceMode' | 'totalPrice' | 'default';
  onlySign?: boolean | false;
  displayInline?: boolean | false;
  customPrefix?: string;
  customSuffix?: string;
  normalFont?: boolean | false;
  className?: string;
  displayPriceSuffixExtraline?: boolean;
  deltaReference?: ApiPrice;
}

export const PriceListItemLayout = observer((props: Props) => {
  const { t } = useTranslation('package', fallback, Keys);
  const config = useConfig();
  const {
    pricePerDisplay,
    pricePer,
    showTaxText,
    customTaxText,
    showFromPrefix,
    showSign,
    mode = 'default',
    onlySign,
    displayInline,
    displayPriceSuffixExtraline,
    customPrefix = '',
    customSuffix,
    normalFont,
    className,
    deltaReference
  } = props;

  let pricePerLabel;
  if (pricePerDisplay === PricePerDisplay.DAY) {
    pricePerLabel = t(Keys.pricePerDay);
  } else if (pricePerDisplay === PricePerDisplay.PASSENGER) {
    pricePerLabel = t(Keys.pricePerPassenger);
  } else if (pricePerDisplay) {
    pricePerLabel = pricePerDisplay;
  }

  let pricePrefix = `${customPrefix} `;
  if (!onlySign && customPrefix.length < 1) {
    pricePrefix =
      props.priceType === undefined && !showFromPrefix
        ? `${t(Keys.totalPrice)} ${props.isTotalFromPrice ? ` ${t(Keys.from)} ` : ``}`
        : showFromPrefix
        ? ` ${t(Keys.from)} `
        : '';
  }

  let price = { ...props.totalPrice };
  if (!!deltaReference) {
    price = PackageCustomizerUtil.getDeltaPrice(price, deltaReference);
  }

  let priceSuffix = '';
  if (props.priceType === ApiPriceType.UNIT) {
    priceSuffix = t(Keys.perUnit);
  } else if (props.priceType === ApiPriceType.PERSON) {
    priceSuffix = t(Keys.perAdult);

    const participantModifiers = price.modifiers.filter(
      m => m.type === ApiPriceModifierType.PARTICIPANT
    );
    const deltaParticipantModifier = deltaReference?.modifiers?.find(
      m => m.type === ApiPriceModifierType.PARTICIPANT
    );

    if (participantModifiers.length) {
      price.finalPrice = participantModifiers[0].absolute;
      if (!!deltaParticipantModifier) {
        price.finalPrice -= deltaParticipantModifier.absolute;
      }
    }
  }

  function getPrefix(): string {
    return props.totalPrice && props.totalPrice.finalPrice < 0 ? ` ` : `+ `;
  }

  return (
    <div className={`iso__price-list-item-layout ${className || ''}`}>
      {mode !== 'totalPrice' && pricePerLabel && pricePer && (
        <>
          <div className="iso__price-list-item-layout__pricePerLabel">{pricePerLabel}</div>
          <div className="iso__price-list-item-layout__pricePerPrice  pb-1 font-weight-bold price-per-person">
            <Price
              price={pricePer}
              className={mode === 'priceMode' ? 'iso__price-list-item-layout__price-left' : ''}
              roundPrice={config.roundPricesToWholeNumber}
            />
          </div>
        </>
      )}
      {mode !== 'priceMode' && props.totalPrice && (
        <>
          <div>
            {!displayInline && pricePrefix}
            <div className={normalFont ? '' : 'font-weight-bold'}>
              {displayInline && pricePrefix}
              <Price
                displayInline
                prefix={showSign ? getPrefix() : ' '}
                price={price}
                roundPrice={config.roundPricesToWholeNumber}
              />
              {!displayPriceSuffixExtraline && displayInline && (customSuffix || priceSuffix)}
            </div>
            <div>
              {(displayPriceSuffixExtraline || !displayInline) && (customSuffix || priceSuffix)}
            </div>
          </div>
          {showTaxText && (
            <div className="iso__price-list-item-layout__taxText">
              {customTaxText || t(Keys.taxText)}
            </div>
          )}
        </>
      )}
    </div>
  );
});
